import { useEffect, useState, useCallback, useRef } from "react";

import { useApi } from "../../hooks/useApi";
import { useAuth } from "../authContext";
import { useGlobal } from "../globalContext";

import useIOWS from "./useIOWS";

// DO NOT use this hook directly in components or other hooks
// this is an internal hook that should be only used by JoinedMeetingScreenIOContextProvider, to provide centralized state management and logic control
export default function useCentralLogics(IOType, IOWSEnabledForDebugging) {
  const { userData } = useAuth();
  const { SCREEN_STATE, MEETING_STATE, JANUS_REF } = useGlobal();

  // for all of the following states (ending with xxxScreenId), falsy values means no control over any screen, otherwise it's the screen id
  // maintained in frontend by user's choice
  const [localRequestingControlScreenId, setLocalRequestingControlScreenId] = useState();
  // in sync with whatever reported by backend through SSE
  const [backendReportingControls, setBackendReportingControls] = useState({}); // { screenId: userId }
  // based on the above two (auto updated in useEffect)
  const [controlScreenId, setControlScreenId] = useState();

  // based on the above states and current screenId (auto updated in useEffect)
  const [userHasRequestedControlOfCurrentScreen, setUserHasRequestedControlOfCurrentScreen] = useState(false);
  const [userHasControlOfCurrentScreen, setUserHasControlOfCurrentScreen] = useState(false);

  const [lastSentCommand, setLastSentCommand] = useState();
  // object with command prop ({ command }), so setting state with a new object with the same command as before, will still trigger useEffect

  useEffect(() => {
    if (localRequestingControlScreenId) {
      if (String(backendReportingControls[localRequestingControlScreenId]) === userData.userId) {
        // we got what we requested
        setControlScreenId(localRequestingControlScreenId);
      }
      // we didn't get it yet
      else setControlScreenId();
    }
    // we don't want anything
    else setControlScreenId();
  }, [localRequestingControlScreenId, backendReportingControls, userData.userId]);

  useEffect(() => {
    const hasRequestedControl = String(localRequestingControlScreenId) === String(SCREEN_STATE.currentScreen.id);
    setUserHasRequestedControlOfCurrentScreen(hasRequestedControl);
  }, [localRequestingControlScreenId, SCREEN_STATE.currentScreen.id]);

  useEffect(() => {
    const hasControl = String(controlScreenId) === String(SCREEN_STATE.currentScreen.id);
    setUserHasControlOfCurrentScreen(hasControl);
    if (hasControl) setLastSentCommand({ command: "connected" });
  }, [controlScreenId, SCREEN_STATE.currentScreen.id]);

  const { screenIOAction } = useApi();

  const requestControl = useCallback(async () => {
    if (!MEETING_STATE.activeMeetingId || !SCREEN_STATE.currentScreen.id) return;
    await screenIOAction(MEETING_STATE.activeMeetingId, {
      screenId: SCREEN_STATE.currentScreen.id,
      action: `${IOType}-take-over`,
    });
    setLocalRequestingControlScreenId(SCREEN_STATE.currentScreen.id);
  }, [MEETING_STATE.activeMeetingId, SCREEN_STATE.currentScreen.id]);

  const releaseControl = useCallback(async () => {
    if (localRequestingControlScreenId) {
      if (String(backendReportingControls[localRequestingControlScreenId]) === userData.userId) {
        // we got what we requested
        await screenIOAction(MEETING_STATE.activeMeetingId, {
          screenId: localRequestingControlScreenId,
          action: `${IOType}-release`,
        });
      }

      setLocalRequestingControlScreenId();
    }
  }, [localRequestingControlScreenId, backendReportingControls, MEETING_STATE.activeMeetingId, userData.userId]);

  useEffect(() => {
    // if current screen changes, we need to release control of the previous screen
    releaseControl();
  }, [SCREEN_STATE.currentScreen.id]);

  const wsRef = useRef();
  const { send } = useIOWS({
    IOWSEnabledForDebugging,
    userHasControlOfCurrentScreen,
    wsRef,
    IOType,
  }); // ?????????????????? does send change? if yes add to next useCallback deps

  const sendCommand = useCallback(
    (command) => {
      if (userHasControlOfCurrentScreen) {
        console.log("sendCommand", command);
        JANUS_REF.current?.plugin?.videoRoom?.data({ text: command });
        if (IOWSEnabledForDebugging) send(command);
      } else console.log("Ignoring sendCommand", command);
    },
    [userHasControlOfCurrentScreen, IOWSEnabledForDebugging],
  );

  const setIconState = useCallback(
    (command) => {
      if (userHasControlOfCurrentScreen) {
        console.log("setIconState", command);
        setLastSentCommand({ command });
      } else console.log("Ignoring setIconState", command);
    },
    [userHasControlOfCurrentScreen],
  );

  return [
    localRequestingControlScreenId,
    setLocalRequestingControlScreenId,
    backendReportingControls,
    setBackendReportingControls,
    userHasRequestedControlOfCurrentScreen,
    userHasControlOfCurrentScreen,
    lastSentCommand,
    requestControl,
    releaseControl,
    sendCommand,
    setIconState,
  ];
}
